import isEqual from "lodash/isEqual";
import { matchSorter } from "match-sorter";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { RailsId, Speaker, Sponsor, Vendor } from "src/types";

import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import useAnalytics from "@/hooks/use-analytics";

import { CheckmarkCircle } from "../icons/checkmark-circle";
import { Input } from "../shared/forms";
import { ToastContent } from "../toasts/toast-content-component";
import {
  CircleImage,
  Label,
  StandardImage,
} from "./import-list-component-styles";

export type ImportableTypes = Sponsor | Speaker | Vendor;

export type ImportListProps = {
  type: "speakers" | "sponsors" | "vendors";
  list: ImportableTypes[];
  onAddSelected: (args: ImportableTypes[]) => void;
  informationText?: string;
  title: string;
};

export default function ImportList(props: ImportListProps) {
  const { logAnalytics } = useAnalytics();
  const { onAddSelected } = props;
  const { t } = useTranslation();

  const [selectedIds, setSelectedIds] = useState<RailsId[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<ImportableTypes[]>([]);

  const handleAddSelection = useCallback(() => {
    const args = props.list.filter((entry) => {
      return selectedIds.includes(entry.id.toString());
    });

    onAddSelected(args);

    toast.success(
      <ToastContent
        icon={CheckmarkCircle}
        text={t("preference-panel.successful-data-import")}
      />
    );

    logAnalytics("CANVAS_EDITOR_DATA_IMPORTED", {
      block_type: props.type,
    });
  }, [props.list, props.type, selectedIds, onAddSelected, logAnalytics, t]);

  const handleChange = useCallback(
    (event) => {
      const { value: selectedId } = event.target;
      if (selectedIds.includes(selectedId)) {
        setSelectedIds(selectedIds.filter((id) => id !== selectedId));
      } else {
        setSelectedIds([...selectedIds, selectedId]);
      }
    },
    [selectedIds]
  );

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.currentTarget.value.toLowerCase());
  }, []);

  useEffect(() => {
    const results = matchSorter(props.list, searchTerm, { keys: ["name"] });
    setSearchResults(results);
  }, [searchTerm, props.list]);

  const allItemsSelected = useMemo(() => {
    return isEqual(
      searchResults.map((result) => String(result.id)),
      selectedIds
    );
  }, [searchResults, selectedIds]);

  return (
    <React.Fragment>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-medium leading-7" data-testid="component-name">
          {props.title}
        </h2>
        {searchResults.length !== 0 && (
          <Button
            onClick={() =>
              setSelectedIds(
                allItemsSelected
                  ? []
                  : searchResults.map((res) => String(res.id))
              )
            }
            variant="ghost"
            isInline
            size="tiny"
          >
            {allItemsSelected
              ? t("common.clear-all")
              : t("preference-panel.select-all")}
          </Button>
        )}
      </div>
      <p
        className="mb-3 text-xs leading-4 text-gray-600"
        data-testid="information-text"
      >
        {props.informationText
          ? props.informationText
          : t("preference-panel.pick-type-from-event", { type: props.type })}
      </p>
      <Input
        name="search"
        aria-label={t("preference-panel.search")}
        label={t("preference-panel.search")}
        value={searchTerm}
        onChange={handleSearch}
        data-testid="search-bar"
        inputClassName="h-8 text-xs"
        className="pb-4"
      />
      {searchResults &&
        searchResults.map((data) => {
          const id = `${data.id}-${data.name}`;
          return (
            <Label htmlFor={id} key={id}>
              {data.image_url &&
                (props.type === "speakers" ? (
                  <CircleImage src={data.image_url} alt={data.name} />
                ) : (
                  <StandardImage src={data.image_url} alt={data.name} />
                ))}
              <p className="text-xs">{data.name}</p>
              <Checkbox
                onChange={handleChange}
                label=""
                id={id}
                value={data.id.toString()}
                checked={selectedIds.includes(data.id.toString())}
              />
            </Label>
          );
        })}

      <div className="flex justify-center">
        <Button
          data-testid="add-selection-button"
          size="small"
          disabled={selectedIds.length === 0}
          onClick={handleAddSelection}
        >
          {t("preference-panel.add-type", {
            type:
              props.type === "vendors"
                ? t("preference-panel.expo.booths-lowercase")
                : props.type === "speakers"
                ? t("preference-panel.speakers.speakers-lowercase")
                : t("preference-panel.sponsors.sponsors-lowercase"),
          })}
        </Button>
      </div>
    </React.Fragment>
  );
}
